import { defineStore } from "pinia";
import api from "@/services/api";
import { type Filters, useCompanyFacilities } from "@/stores/facilities";
import type { FacilityEmissionSourceFootprint } from "@/stores/carbonFootprint";
import type { ChartData } from "@/stores/comparasion";
import { useTarget } from "@/stores/target";
import type { EmissionSourceUnit } from "@/stores/companyEmissionSource";
import type { User } from "@/types";

export interface AnalysisEmissionSourceFootprint{
  activityData: number;
  activityUnit: EmissionSourceUnit;
  co2Equivalent: number;
  co2: number;
  n20: number;
  year: string;
  month?: string;
  startDate?: string;
  endDate?: string;
  carbonFootprintPeriod: string;
  consumptionExplanation?: string;
  emmisionGroup: string;
  emissionSource: string;
  createdBy: User;
  emissionFactorDatabase: string;
  emissionFactor: number;
}

export interface TimeSeriesChart {
  period: string,
  label: string,
  name: string,
  value: number
}

export interface AnalyticChartData {
  label: string,
  name: string,
  value: number,
  percentage: number
}

interface FacilityPercentage {
  facilityId: string,
  percentage: number
}

export interface StackedCharData {
  breakDown: string,
  groupBy: string,
  breakDownRaw: string,
  groupByRaw: string,
  value: number,
}

export interface AnalysisReport {
  graphName: string;
  description?: string;
  graphType: string;
  graphTotal?: number;
  scope: string;
  breakdown: string;
  reportGraphItemList: ChartData[];
  facility: string[];
}

type State = {
  facilityEmissionSourceFootprint: {
    content: FacilityEmissionSourceFootprint[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
  };
  breakdown: string;
  groupBy: string,
  carbonFootprintPeriod: string;
  year: string;
  scope: string;
  scopes: string[],
  facilities: string[];
  customDate: string;
  analysis: [];
  dataList: ChartData[];
  isLoading: boolean;
  reportSections: AnalysisReport[];
  error: string;
  total: number;
  activeComponent: string;
  timeSeries: TimeSeriesChart[];
  intensity: string | null;
  facilityPercentage: FacilityPercentage[];
  totalCompanyPercentage: number;
  responseUnit: string;
  isError: boolean;
  groupedDataList: StackedCharData[],
  tags: string[],
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  currentFirstFilter: string | null;
  currentSecondFilter: string | null;
  emissionFactorType: string | null;
};


export const useAnalytic = defineStore("analytic", {
  state: (): State => ({
    facilityEmissionSourceFootprint: {
      content: [],
      totalElements: 0,
      totalPages: 0,
      number: 0,
      size: 0
    },
    breakdown: "SCOPE",
    groupBy: '' as string,
    carbonFootprintPeriod: "ANNUAL",
    year: "" as string,
    scope: "" as string,
    scopes: [] as string[],
    facilities: [],
    customDate: "",
    analysis: [],
    dataList: [] as ChartData[],
    isLoading: false,
    reportSections: [],
    error: "",
    total: 0,
    activeComponent: "AnalysisDefault",
    timeSeries: [] as TimeSeriesChart[],
    intensity: "" as string | null,
    facilityPercentage: [] as FacilityPercentage[],
    totalCompanyPercentage: 0,
    responseUnit: "tCO2e",
    isError: false,
    groupedDataList: [] as StackedCharData[],
    tags: [] as string[],
    page: 0,
    size: 10,
    totalElements: 0,
    totalPages: 0,
    currentFirstFilter: null,
    currentSecondFilter: null,
    emissionFactorType: null,
  }),
  actions: {
    async fetchAnalysis() {
      const facilityStore = useCompanyFacilities();

      this.isLoading = true;
      const companyId = localStorage.getItem("companyId");
      let filter = `?breakdown=${this.breakdown}`;

      if (this.carbonFootprintPeriod) {
        filter += '&carbonFootprintPeriod=ANNUAL';
      }

      if (this.year) {
        filter += `&year=${this.year}`;
      }

      if (this.scopes.length !== 0) {
        filter += `&scopeList=${this.scopes}`
      }

      if (this.scope) {
        filter += `&scopeList=${this.scope}`;
      }

      if (this.intensity) {
        filter += `&intensityType=${this.intensity}`;
      }

      if (this.customDate !== "") {
        const dates = this.customDate.split("~");
        filter += `&startDate=${dates[0]}&endDate=${dates[1]}`;
      }

      if (this.groupBy) {
        filter += `&groupBy=${this.groupBy}`
      }

      if (this.emissionFactorType != null) {
        filter += `&emissionFactorType=${this.emissionFactorType}`
      }


      if (this.facilities.length !== 0) {
        this.facilities.forEach((id: string) => {
          filter += `&facilityIdList=${id}`;
        });
      }


      if (
        facilityStore.filters.facilityTypes &&
        facilityStore.filters.facilityTypes.length > 0
      ) {
        facilityStore.filters.facilityTypes.forEach((facilityType: any) => {
          filter+= `&facilityTypes=${facilityType.facilityTypeName}`;
        });
      }

      if (
        facilityStore.filters.countryCodes &&
        facilityStore.filters.countryCodes.length > 0 &&
        !(facilityStore.filters.countryCodes[0] === "string")
      ) {
        facilityStore.filters.countryCodes.forEach((country: any) => {
          filter+= `&countryCodes=${country}`;
        });
      }

      if (
        facilityStore.filters.tags &&
        facilityStore.filters.tags.length > 0
      ) {
        facilityStore.filters.tags.forEach((tag: string) => {
          filter+= `&tags=${tag}`;
        });
      }

      if (facilityStore.filters.calculationStatus && facilityStore.filters.calculationStatus!=='Show all') {
        filter+= `&calculationStatus=${facilityStore.filters.calculationStatus}`;
      }

      if (facilityStore.filters.name) {
        filter+= `&facilityName=${facilityStore.filters.name}`;
      }

      try {
        const { data } = await api.get(
          `company/${companyId}/carbon-footprint/summary${filter}`
        );

        if (this.groupBy) {
          this.groupedDataList = data.dataList;
          return
        }

        this.analysis = data;
        this.dataList = data.dataList;

        this.responseUnit = data.unit;
        this.total = data.total;


        this.isLoading = false;
      } catch (e) {
        this.analysis = [];
        this.dataList = [];
        this.responseUnit = "none";
        this.total = 0;
        this.isLoading = false;
      }
    },
    async fetchTimeSeries() {
      const facilityStore = useCompanyFacilities();
      this.isError = false;
      this.isLoading = true;
      const companyId: string = <string>localStorage.getItem("companyId");


      let filter: string = `?carbonFootprintPeriod=${this.carbonFootprintPeriod}`;

      if (this.facilities.length !== 0) {
        this.facilities.forEach((id: string) => {
          filter += `&facilityIdList=${id}`;
        });
      }

      if (this.customDate !== "") {
        const dates = this.customDate.split("~");

        filter += `&startDate=${dates[0]}&endDate=${dates[1]}`;
      }

      if (this.scopes.length !== 0) {
        filter += `&scopeList=${this.scopes}`
      }

      if (this.year) {
        filter += `&year=${this.year}`;
      }

      if (this.intensity) {
        filter += `&intensityType=${this.intensity}`;
      }

      if (this.emissionFactorType != null) {
        filter += `&emissionFactorType=${this.emissionFactorType}`;
      }

      if (
        facilityStore.filters.facilityTypes &&
        facilityStore.filters.facilityTypes.length > 0
      ) {
        facilityStore.filters.facilityTypes.forEach((facilityType: any) => {
          filter+= `&facilityTypes=${facilityType.facilityTypeName}`;
        });
      }

      if (
        facilityStore.filters.countryCodes &&
        facilityStore.filters.countryCodes.length > 0 &&
        !(facilityStore.filters.countryCodes[0] === "string")
      ) {
        facilityStore.filters.countryCodes.forEach((country: any) => {
          filter+= `&countryCodes=${country}`;
        });
      }

      if (
        facilityStore.filters.tags &&
        facilityStore.filters.tags.length > 0
      ) {
        facilityStore.filters.tags.forEach((tag: string) => {
          filter+= `&tags=${tag}`;
        });
      }

      if (facilityStore.filters.calculationStatus) {
        filter+= `&calculationStatus=${facilityStore.filters.calculationStatus}`;
      }

      if (facilityStore.filters.name) {
        filter+= `&facilityName=${facilityStore.filters.name}`;
      }


      const target = useTarget();

      if (target.selectedEmissionIds.length !== 0) {
        target.selectedEmissionIds.map((id) => {
          filter += `&emissionGroupIdList=${id}`;
        });
      }


      try {
        const { data } = await api.get(
          `company/${companyId}/carbon-footprint/summary/time-series${filter}`
        );

        this.timeSeries = this.mapTimeSeriesResponse(data.dataList);
      } catch (e) {
        this.isError = true;
      }
    },
    /**
     * Map the fields in the response to the TimeSeriesChart type.
     * @param data The response data to be mapped.
     * @returns An array of TimeSeriesChart objects.
     */
    mapTimeSeriesResponse(data: any[]): TimeSeriesChart[] {
      return data.map((item) => ({
        period: item.groupBy || "Unknown Period",
        label: item.label || "Unknown Label",
        name: item.breakdown || "Unknown Name",
        value: item.value || 0,
      }));
    },
    async fetchFilteredEmissionSourceFootprint(firstFilterValue: string | null = null,
                                               secondFilterValue: string | null = null) {
      this.isLoading = true;
      const companyId = localStorage.getItem("companyId");

      const params = new URLSearchParams();

      if (this.facilities.length !== 0) {
        this.facilities.forEach(id => {
          params.append('facilityIdList', id);
        });
      }

      if (firstFilterValue !== null) {
        this.currentFirstFilter = firstFilterValue;
      }
      if (secondFilterValue !== null) {
        this.currentSecondFilter = secondFilterValue;
      }

      params.append('breakdown', this.breakdown);
      if(this.year){
        params.append('year',this.year);
      }
      if(this.groupBy){
        params.append("filterTypes",this.groupBy)
      }
      if(this.breakdown){
        params.append("filterTypes",this.breakdown)
      }
      if (this.currentFirstFilter) {
        params.append("filterValues", this.currentFirstFilter);
      }
      if (this.currentSecondFilter) {
        params.append("filterValues", this.currentSecondFilter); 
      }

      try {
        const { data } = await api.get(
          `company/${companyId}/carbon-footprint/summary/filtered-footprint?${params.toString()}&page=${this.page}&size=${this.size}`
        );
        this.facilityEmissionSourceFootprint = data.content;
        this.totalElements = data.totalElements;
        this.totalPages = data.totalPages;
        this.page = data.number;

        this.isLoading = false;
      } catch (e) {
        this.totalElements = 0;
        this.totalPages = 0;
        this.isLoading = false;
      }
    },

    async setPage(newPage: number) {
      console.log(`Setting page from ${this.page} to ${newPage}`);
      this.page = newPage;
      return await this.fetchFilteredEmissionSourceFootprint();
    },
    
    async setPageSize(newSize: number) {
      console.log(`Setting page size from ${this.size} to ${newSize}`);
      this.size = newSize;
      this.page = 0;
      return await this.fetchFilteredEmissionSourceFootprint();
    },

    addFacility(facilityId: string) {
      if (this.facilities.length === 0) {
        this.facilities.push(facilityId);
        return;
      }

      const checkId = this.facilities.includes(facilityId);

      if (checkId) {
        const index = this.facilities.indexOf(facilityId);

        this.facilities.splice(index, 1);
        return;
      }

      this.facilities.push(facilityId);
    },
    async fetchFacilityPercentage(baseYear: string | null = null) {
      const companyId = localStorage.getItem("companyId");
    
      let filter = "";

      if (this.year && baseYear === null) {
        filter = `?year=${this.year}`;
      } else if (baseYear !== null) {
        filter = `?baseYear=${baseYear}`;
      }

      const facilityStore = useCompanyFacilities();

      if (this.scopes.length !== 0) {
        filter += filter ? `&scopeList=${this.scopes}` : `?scopeList=${this.scopes}`;
      }

      if (
        facilityStore.filters.facilityTypes &&
        facilityStore.filters.facilityTypes.length > 0
      ) {
        facilityStore.filters.facilityTypes.forEach((facilityType: any) => {
          filter += filter ? `&facilityTypes=${facilityType.facilityTypeName}` : `?facilityTypes=${facilityType.facilityTypeName}`;
        });
      }

      if (
        facilityStore.filters.countryCodes &&
        facilityStore.filters.countryCodes.length > 0 &&
        !(facilityStore.filters.countryCodes[0] === "string")
      ) {
        facilityStore.filters.countryCodes.forEach((country: any) => {
          filter += filter ? `&countryCodes=${country}` : `?countryCodes=${country}`;
        });
      }

      if (
        facilityStore.filters.tags &&
        facilityStore.filters.tags.length > 0
      ) {
        facilityStore.filters.tags.forEach((tag: string) => {
          filter += filter ? `&tags=${tag}` : `?tags=${tag}`;
        });
      }

      if (facilityStore.filters.calculationStatus && facilityStore.filters.calculationStatus !== 'Show all') {
        filter += filter ? `&calculationStatus=${facilityStore.filters.calculationStatus}` : `?calculationStatus=${facilityStore.filters.calculationStatus}`;
      }

      if (facilityStore.filters.name) {
        filter += filter ? `&facilityName=${facilityStore.filters.name}` : `?facilityName=${facilityStore.filters.name}`;
      }
    
      if (
        facilityStore.filters.facilityTypes &&
        facilityStore.filters.facilityTypes.length > 0
      ) {
        facilityStore.filters.facilityTypes.forEach((facilityType: any) => {
          filter += filter ? `&facilityTypes=${facilityType.facilityTypeName}` : `?facilityTypes=${facilityType.facilityTypeName}`;
        });
      }
    
      if (
        facilityStore.filters.countryCodes &&
        facilityStore.filters.countryCodes.length > 0 &&
        !(facilityStore.filters.countryCodes[0] === "string")
      ) {
        facilityStore.filters.countryCodes.forEach((country: any) => {
          filter += filter ? `&countryCodes=${country}` : `?countryCodes=${country}`;
        });
      }
    
      if (
        facilityStore.filters.tags &&
        facilityStore.filters.tags.length > 0
      ) {
        facilityStore.filters.tags.forEach((tag: string) => {
          filter += filter ? `&tags=${tag}` : `?tags=${tag}`;
        });
      }
    
      if (facilityStore.filters.calculationStatus && facilityStore.filters.calculationStatus !== 'Show all') {
        filter += filter ? `&calculationStatus=${facilityStore.filters.calculationStatus}` : `?calculationStatus=${facilityStore.filters.calculationStatus}`;
      }
    
      if (facilityStore.filters.name) {
        filter += filter ? `&facilityName=${facilityStore.filters.name}` : `?facilityName=${facilityStore.filters.name}`;
      }
    
      try {
        const { data } = await api.get(
          `company/${companyId}/facility-percentage${filter}`
        );
        this.facilityPercentage = data.facilityEmissionPercentageList;
        this.totalCompanyPercentage = data.totalPercentage;
      } catch (e) {
        this.facilityPercentage = [];
        this.totalCompanyPercentage = 0;
      }
    },
    storeReportSections(data: AnalysisReport) {
      //@ts-ignore
      this.reportSections.push(data);
    },
    removeReportSection(index: number) {
      this.reportSections.splice(index, 1);
    },
    resetReportSections() {
      this.reportSections = [];
    },
  },
  getters: {
    getFacilityPercentage: (state) => (facilityId: string) => {
      const percentage: FacilityPercentage | undefined =
        state.facilityPercentage.find((f) => f.facilityId === facilityId);
      return percentage ? percentage.percentage : 0;
    }
  },
});
